'use client';

import { createClient } from '@/lib/supabase/client';
import { getCurrentWeekId } from './weekUtils';

// ── Types ─────────────────────────────────────────────────────
export interface Team {
  id: string;
  name: string;
  createdAt: string;
}

export interface GembaUser {
  id: string;
  name: string;
  email: string;
  teamId: string | null;
  role: 'admin' | 'leader' | 'member';
  position?: string | null;
  authUserId?: string | null;
  createdAt: string;
}

export interface CheckIn {
  id: string;
  userId: string;
  teamId: string;
  week: string;
  weekId: string;
  mood: number;
  stress: number;
  connection: number;
  note?: string;
  createdAt: string;
  gemba_users?: { name: string; email: string };
}

export interface InsightMemory {
  id: string;
  userId: string;
  insightType: 'positive' | 'negative';
  content: string;
  createdAt: string;
}

export interface AISuggestion {
  id: string;
  teamId: string;
  message: string;
  suggestionType: 'connection' | 'wellbeing' | 'recognition';
  createdAt: string;
}

export interface AccessRequest {
  id: string;
  name: string;
  email: string;
  company?: string;
  teamName?: string;
  teamSize?: string;
  marketingConsent?: boolean;
  reqStatus: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface TeamInvite {
  id: string;
  teamId: string;
  email: string;
  token: string;
  invStatus: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
  createdAt: string;
}

// ── Teams ─────────────────────────────────────────────────────
export const teamsService = {
  async getAll(): Promise<Team[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) { console.error('teamsService.getAll:', error.message); return []; }
    return (data || []).map(r => ({ id: r.id, name: r.name, createdAt: r.created_at }));
  },

  async create(name: string): Promise<Team | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('teams')
      .insert({ name })
      .select()
      .single();
    if (error) { console.error('teamsService.create:', error.message); return null; }
    return { id: data.id, name: data.name, createdAt: data.created_at };
  },

  async remove(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('teams').delete().eq('id', id);
    if (error) { console.error('teamsService.remove:', error.message); return false; }
    return true;
  },
};

// ── Gemba Users ───────────────────────────────────────────────
export const gembaUsersService = {
  async getAll(): Promise<GembaUser[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('gemba_users')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) { console.error('gembaUsersService.getAll:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, name: r.name, email: r.email,
      teamId: r.team_id, role: r.role, position: r.position ?? null, authUserId: r.auth_user_id ?? null, createdAt: r.created_at,
    }));
  },

  async getByTeam(teamId: string): Promise<GembaUser[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('gemba_users')
      .select('*')
      .eq('team_id', teamId);
    if (error) { console.error('gembaUsersService.getByTeam:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, name: r.name, email: r.email,
      teamId: r.team_id, role: r.role, position: r.position ?? null, authUserId: r.auth_user_id ?? null, createdAt: r.created_at,
    }));
  },

  async create(user: { name: string; email: string; teamId?: string; role?: string; isCreator?: boolean }): Promise<GembaUser | null> {
    const supabase = createClient();
    // Safeguard: if user is the creator of the team, always force role = 'leader'
    const resolvedRole = user.isCreator ? 'leader' : (user.role || 'member');
    const { data, error } = await supabase
      .from('gemba_users')
      .insert({ name: user.name, email: user.email, team_id: user.teamId || null, role: resolvedRole })
      .select()
      .single();
    if (error) { console.error('gembaUsersService.create:', error.message); return null; }
    return { id: data.id, name: data.name, email: data.email, teamId: data.team_id, role: data.role, position: data.position ?? null, authUserId: data.auth_user_id ?? null, createdAt: data.created_at };
  },

  async updateTeam(userId: string, teamId: string | null): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('gemba_users')
      .update({ team_id: teamId })
      .eq('id', userId);
    if (error) { console.error('gembaUsersService.updateTeam:', error.message); return false; }
    return true;
  },

  async remove(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('gemba_users').delete().eq('id', id);
    if (error) { console.error('gembaUsersService.remove:', error.message); return false; }
    return true;
  },
};

// ── Check-ins ─────────────────────────────────────────────────
export const checkInsService = {
  async getByTeam(teamId: string, weekId?: string): Promise<CheckIn[]> {
    const supabase = createClient();
    let query = supabase
      .from('check_ins')
      .select('*, gemba_users(name, email)')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });
    if (weekId) query = query.eq('week_id', weekId);
    const { data, error } = await query;
    if (error) { console.error('checkInsService.getByTeam:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, userId: r.user_id, teamId: r.team_id, week: r.week, weekId: r.week_id,
      mood: r.mood, stress: r.stress, connection: r.connection,
      note: r.note, createdAt: r.created_at, gemba_users: r.gemba_users,
    }));
  },

  /**
   * Check if a specific user has already checked in for the current week.
   * Uses week_id (YYYY-WW) for accurate deduplication.
   */
  async hasCheckedInThisWeek(userId: string): Promise<boolean> {
    const supabase = createClient();
    const weekId = getCurrentWeekId();
    const { data, error } = await supabase
      .from('check_ins')
      .select('id')
      .eq('user_id', userId)
      .eq('week_id', weekId)
      .maybeSingle();
    if (error) { console.error('checkInsService.hasCheckedInThisWeek:', error.message); return false; }
    return data !== null;
  },

  /**
   * Get the current week's check-in for a specific user (from DB).
   */
  async getCurrentWeekCheckIn(userId: string): Promise<CheckIn | null> {
    const supabase = createClient();
    const weekId = getCurrentWeekId();
    const { data, error } = await supabase
      .from('check_ins').select('*').eq('user_id', userId).eq('week_id', weekId)
      .maybeSingle();
    if (error) { console.error('checkInsService.getCurrentWeekCheckIn:', error.message); return null; }
    if (!data) return null;
    return {
      id: data.id, userId: data.user_id, teamId: data.team_id,
      week: data.week, weekId: data.week_id,
      mood: data.mood, stress: data.stress, connection: data.connection,
      note: data.note, createdAt: data.created_at,
    };
  },

  /**
   * Get past check-ins for a user (previous weeks only, ordered by most recent).
   */
  async getPastCheckIns(userId: string, limit = 10): Promise<CheckIn[]> {
    const supabase = createClient();
    const currentWeekId = getCurrentWeekId();
    const { data, error } = await supabase
      .from('check_ins')
      .select('*')
      .eq('user_id', userId)
      .neq('week_id', currentWeekId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) { console.error('checkInsService.getPastCheckIns:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, userId: r.user_id, teamId: r.team_id,
      week: r.week, weekId: r.week_id,
      mood: r.mood, stress: r.stress, connection: r.connection,
      note: r.note, createdAt: r.created_at,
    }));
  },

  /**
   * Get team averages for a given week_id.
   * Returns unique user count (not total check-ins) for accurate participation rate.
   */
  async getTeamAveragesByWeekId(teamId: string, weekId: string): Promise<{ avgMood: number; avgStress: number; avgConnection: number; uniqueUserCount: number }> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('mood, stress, connection, user_id')
      .eq('team_id', teamId)
      .eq('week_id', weekId);
    if (error || !data?.length) return { avgMood: 0, avgStress: 0, avgConnection: 0, uniqueUserCount: 0 };
    // Count unique users only
    const uniqueUsers = new Set(data.map(r => r.user_id));
    const uniqueUserCount = uniqueUsers.size;
    const avgMood = +(data.reduce((s, r) => s + r.mood, 0) / data.length).toFixed(1);
    const avgStress = +(data.reduce((s, r) => s + r.stress, 0) / data.length).toFixed(1);
    const avgConnection = +(data.reduce((s, r) => s + r.connection, 0) / data.length).toFixed(1);
    return { avgMood, avgStress, avgConnection, uniqueUserCount };
  },

  // Legacy: keep for backward compat — uses week label (e.g. "Uge 12")
  async getTeamAverages(teamId: string, week: string): Promise<{ avgMood: number; avgStress: number; avgConnection: number; count: number }> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('mood, stress, connection, user_id')
      .eq('team_id', teamId)
      .eq('week', week);
    if (error || !data?.length) return { avgMood: 0, avgStress: 0, avgConnection: 0, count: 0 };
    const uniqueUsers = new Set(data.map(r => r.user_id));
    const count = uniqueUsers.size; // unique users, not total rows
    const avgMood = +(data.reduce((s, r) => s + r.mood, 0) / data.length).toFixed(1);
    const avgStress = +(data.reduce((s, r) => s + r.stress, 0) / data.length).toFixed(1);
    const avgConnection = +(data.reduce((s, r) => s + r.connection, 0) / data.length).toFixed(1);
    return { avgMood, avgStress, avgConnection, count };
  },

  async getPrevWeekAverages(teamId: string, prevWeek: string): Promise<{ avgMood: number }> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('mood')
      .eq('team_id', teamId)
      .eq('week', prevWeek);
    if (error || !data?.length) return { avgMood: 0 };
    const avgMood = +(data.reduce((s, r) => s + r.mood, 0) / data.length).toFixed(1);
    return { avgMood };
  },

  /**
   * Get previous week averages using week_id (YYYY-WW) for accurate ISO week filtering.
   * Prevents mixing weeks and avoids future week data.
   */
  async getPrevWeekAveragesByWeekId(teamId: string, prevWeekId: string): Promise<{ avgMood: number; avgStress: number; avgConnection: number; uniqueUserCount: number }> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('mood, stress, connection, user_id')
      .eq('team_id', teamId)
      .eq('week_id', prevWeekId);
    if (error || !data?.length) return { avgMood: 0, avgStress: 0, avgConnection: 0, uniqueUserCount: 0 };
    const uniqueUsers = new Set(data.map(r => r.user_id));
    const uniqueUserCount = uniqueUsers.size;
    const avgMood = +(data.reduce((s, r) => s + r.mood, 0) / data.length).toFixed(1);
    const avgStress = +(data.reduce((s, r) => s + r.stress, 0) / data.length).toFixed(1);
    const avgConnection = +(data.reduce((s, r) => s + r.connection, 0) / data.length).toFixed(1);
    return { avgMood, avgStress, avgConnection, uniqueUserCount };
  },

  /**
   * Get trend data grouped by week_id for a date range.
   * Returns ONLY weeks that have real data (no fake/placeholder data).
   */
  async getTrendByWeekIds(teamId: string, weekIds: string[]): Promise<{ weekId: string; mood: number; stress: number; connection: number; count: number }[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('week_id, mood, stress, connection')
      .eq('team_id', teamId)
      .in('week_id', weekIds);
    if (error || !data?.length) return [];
    const grouped: Record<string, { mood: number[]; stress: number[]; connection: number[] }> = {};
    data.forEach(r => {
      if (!r.week_id) return;
      if (!grouped[r.week_id]) grouped[r.week_id] = { mood: [], stress: [], connection: [] };
      grouped[r.week_id].mood.push(r.mood);
      grouped[r.week_id].stress.push(r.stress);
      grouped[r.week_id].connection.push(r.connection);
    });
    // Only return weeks that actually have data
    return weekIds
      .filter(w => grouped[w] && grouped[w].mood.length > 0)
      .map(w => ({
        weekId: w,
        mood: +(grouped[w].mood.reduce((s, v) => s + v, 0) / grouped[w].mood.length).toFixed(1),
        stress: +(grouped[w].stress.reduce((s, v) => s + v, 0) / grouped[w].stress.length).toFixed(1),
        connection: +(grouped[w].connection.reduce((s, v) => s + v, 0) / grouped[w].connection.length).toFixed(1),
        count: grouped[w].mood.length,
      }));
  },

  async getTrend(teamId: string, weeks: string[]): Promise<{ week: string; mood: number; stress: number; connection: number }[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('check_ins')
      .select('week, mood, stress, connection')
      .eq('team_id', teamId)
      .in('week', weeks);
    if (error || !data?.length) return [];
    const grouped: Record<string, { mood: number[]; stress: number[]; connection: number[] }> = {};
    data.forEach(r => {
      if (!grouped[r.week]) grouped[r.week] = { mood: [], stress: [], connection: [] };
      grouped[r.week].mood.push(r.mood);
      grouped[r.week].stress.push(r.stress);
      grouped[r.week].connection.push(r.connection);
    });
    return weeks
      .filter(w => grouped[w])
      .map(w => ({
        week: w,
        mood: +(grouped[w].mood.reduce((s, v) => s + v, 0) / grouped[w].mood.length).toFixed(1),
        stress: +(grouped[w].stress.reduce((s, v) => s + v, 0) / grouped[w].stress.length).toFixed(1),
        connection: +(grouped[w].connection.reduce((s, v) => s + v, 0) / grouped[w].connection.length).toFixed(1),
      }));
  },

  /**
   * Submit a check-in with upsert logic:
   * - Generates week_id (YYYY-WW)
   * - If check-in already exists for user+week_id → UPDATE it
   * - If not → INSERT new one
   * This prevents duplicate check-ins per week.
   */
  async submit(payload: { userId: string; teamId: string; week: string; mood: number; stress: number; connection: number; note?: string }): Promise<CheckIn | null> {
    const supabase = createClient();
    const weekId = getCurrentWeekId();

    // Check if check-in already exists for this user this week
    const { data: existing } = await supabase
      .from('check_ins')
      .select('id')
      .eq('user_id', payload.userId)
      .eq('week_id', weekId)
      .maybeSingle();

    let data, error;

    if (existing?.id) {
      // UPDATE existing check-in
      const result = await supabase
        .from('check_ins')
        .update({
          mood: payload.mood,
          stress: payload.stress,
          connection: payload.connection,
          note: payload.note || null,
        })
        .eq('id', existing.id)
        .select()
        .single();
      data = result.data;
      error = result.error;
    } else {
      // INSERT new check-in
      const result = await supabase
        .from('check_ins')
        .insert({
          user_id: payload.userId,
          team_id: payload.teamId,
          week: payload.week,
          week_id: weekId,
          mood: payload.mood,
          stress: payload.stress,
          connection: payload.connection,
          note: payload.note || null,
        })
        .select()
        .single();
      data = result.data;
      error = result.error;
    }

    if (error) { console.error('checkInsService.submit:', error.message); return null; }
    return {
      id: data.id, userId: data.user_id, teamId: data.team_id,
      week: data.week, weekId: data.week_id,
      mood: data.mood, stress: data.stress, connection: data.connection,
      note: data.note, createdAt: data.created_at,
    };
  },
};

// ── Insights Memory ───────────────────────────────────────────
export const insightsMemoryService = {
  async save(userId: string, insightType: 'positive\' | \'negative', content: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('insights_memory')
      .insert({ user_id: userId, insight_type: insightType, content });
    if (error) { console.error('insightsMemoryService.save:', error.message); return false; }
    return true;
  },

  async getByUser(userId: string): Promise<InsightMemory[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('insights_memory')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);
    if (error) { console.error('insightsMemoryService.getByUser:', error.message); return []; }
    return (data || []).map(r => ({ id: r.id, userId: r.user_id, insightType: r.insight_type, content: r.content, createdAt: r.created_at }));
  },
};

// ── AI Suggestions ────────────────────────────────────────────
export const aiSuggestionsService = {
  async getLatestForTeam(teamId: string): Promise<AISuggestion[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('ai_suggestions')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false })
      .limit(5);
    if (error) { console.error('aiSuggestionsService.getLatestForTeam:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, teamId: r.team_id, message: r.message,
      suggestionType: r.suggestion_type, createdAt: r.created_at,
    }));
  },

  async save(teamId: string, message: string, suggestionType: 'connection' | 'wellbeing' | 'recognition'): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('ai_suggestions')
      .insert({ team_id: teamId, message, suggestion_type: suggestionType });
    if (error) { console.error('aiSuggestionsService.save:', error.message); return false; }
    return true;
  },
};

// ── Access Requests ───────────────────────────────────────────
export const accessRequestsService = {
  async submit(payload: { name: string; email: string; company?: string; teamName?: string; teamSize?: string; marketingConsent?: boolean }): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('access_requests')
      .insert({
        name: payload.name,
        email: payload.email,
        company: payload.company || null,
        team_name: payload.teamName || null,
        team_size: payload.teamSize || null,
        marketing_consent: payload.marketingConsent || false,
      });
    if (error) { console.error('accessRequestsService.submit:', error.message); return false; }
    return true;
  },

  async getAll(): Promise<AccessRequest[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('access_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('accessRequestsService.getAll:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, name: r.name, email: r.email,
      company: r.company,
      teamName: r.team_name, teamSize: r.team_size,
      marketingConsent: r.marketing_consent,
      reqStatus: r.req_status, createdAt: r.created_at,
    }));
  },

  async updateStatus(id: string, status: 'approved' | 'rejected'): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('access_requests')
      .update({ req_status: status })
      .eq('id', id);
    if (error) { console.error('accessRequestsService.updateStatus:', error.message); return false; }
    return true;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('access_requests')
      .delete()
      .eq('id', id);
    if (error) { console.error('accessRequestsService.delete:', error.message); return false; }
    return true;
  },
};

// ── Team Invites ──────────────────────────────────────────────
export const teamInvitesService = {
  async create(payload: { teamId: string; email: string; invitedBy?: string }): Promise<TeamInvite | null> {
    const supabase = createClient();
    // Generate a secure random token
    const token = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    const { data, error } = await supabase
      .from('team_invites')
      .insert({
        team_id: payload.teamId,
        email: payload.email,
        token,
        invited_by: payload.invitedBy || null,
      })
      .select()
      .single();

    if (error) {
      console.error('teamInvitesService.create:', error.message);
      return null;
    }

    const invite: TeamInvite = {
      id: data.id,
      teamId: data.team_id,
      email: data.email,
      token: data.token,
      invStatus: data.inv_status,
      expiresAt: data.expires_at,
      createdAt: data.created_at,
    };

    // Log invite link to console (fallback if email is not configured)
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://gemba1650.builtwithrocket.new';
    const inviteLink = `${siteUrl}/accept-invite?token=${invite.token}`;
    console.log(`[GEMBA INVITE] Invite created for ${payload.email}`);
    console.log(`[GEMBA INVITE] Link: ${inviteLink}`);

    return invite;
  },

  async getByTeam(teamId: string): Promise<TeamInvite[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('team_invites')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });
    if (error) { console.error('teamInvitesService.getByTeam:', error.message); return []; }
    return (data || []).map(r => ({
      id: r.id, teamId: r.team_id, email: r.email, token: r.token,
      invStatus: r.inv_status, expiresAt: r.expires_at, createdAt: r.created_at,
    }));
  },

  async getByToken(token: string): Promise<TeamInvite | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('team_invites')
      .select('*')
      .eq('token', token)
      .maybeSingle();
    if (error) { console.error('teamInvitesService.getByToken:', error.message); return null; }
    if (!data) return null;
    return {
      id: data.id, teamId: data.team_id, email: data.email, token: data.token,
      invStatus: data.inv_status, expiresAt: data.expires_at, createdAt: data.created_at,
    };
  },

  async markAccepted(token: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('team_invites')
      .update({ inv_status: 'accepted', accepted_at: new Date().toISOString() })
      .eq('token', token);
    if (error) { console.error('teamInvitesService.markAccepted:', error.message); return false; }
    return true;
  },
};
